import React, { useState, useEffect, useMemo, useRef } from "react";
import { getDisplayType } from "../../../utils/helper";
import Landscape from "./components/Landscape";
import Portrait from "./components/Portrait";
import Square from "./components/Square";
import Footer from "./components/Footer";
import {
  statusInfo,
  fmtTime,
  fmtDate,
  fmtClock,
  parseFlight,
  placeParts,
  placeShort,
  fmtGate,
  fmtTerminal,
} from "./components/parts";

const PAGE_MS = 8000;

const LAYOUTS = {
  landscape: Landscape,
  portrait: Portrait,
  square: Square,
  footer: Footer,
  "mini-footer": Footer,
};

const PAGE_SIZE = {
  landscape: 5,
  square: 5,
  portrait: 7,
  footer: 5,
  "mini-footer": 4,
};

const DUAL_PAGE_SIZE = {
  landscape: 10,
  square: 10,
  portrait: 10,
  footer: 3,
  "mini-footer": 3,
};

const splitQuota = (total, depAvail, arrAvail) => {
  const n = Math.max(0, Number(total) || 0);
  const dMax = Math.max(0, Number(depAvail) || 0);
  const aMax = Math.max(0, Number(arrAvail) || 0);

  if (!n || (!dMax && !aMax)) return { dep: 0, arr: 0 };
  if (!aMax) return { dep: Math.min(n, dMax), arr: 0 };
  if (!dMax) return { dep: 0, arr: Math.min(n, aMax) };

  if (n === 1) return { dep: 1, arr: 0 };

  let dep = Math.floor(n / 2);
  let arr = n - dep;
  if (dep < 1) dep = 1;
  if (arr < 1) arr = 1;

  dep = Math.min(dep, dMax);
  arr = Math.min(arr, aMax);

  let rem = n - dep - arr;
  if (rem > 0) {
    const addD = Math.min(rem, dMax - dep);
    dep += addD;
    rem -= addD;
    arr += Math.min(rem, aMax - arr);
  }

  if (dep === 0 && dMax) {
    dep = 1;
    arr = Math.min(aMax, Math.max(0, n - 1));
  }
  if (arr === 0 && aMax) {
    arr = 1;
    dep = Math.min(dMax, Math.max(0, n - 1));
  }

  return { dep, arr };
};

const mapRows = (list, isDep) =>
  list.map((f) => {
    const spot = isDep ? f.destination : f.origin;
    const parts = placeParts(spot);
    const flight = parseFlight(f.flightNumber);
    const est = f.actual || f.estimated || f.scheduled;
    return {
      time: fmtTime(f.scheduled),
      est: fmtTime(est),
      carrier: flight.carrier,
      flight: flight.full,
      toName: parts.name,
      toCode: parts.code,
      toTitle: placeParts(spot).name + (parts.code ? ` (${parts.code})` : ""),
      toCodeShort: placeShort(spot),
      terminal: fmtTerminal(f.terminal),
      gate: fmtGate(f.gate),
      status: statusInfo(f.status, isDep),
    };
  });

const Flight = ({ widget }) => {
  const flights = (widget && widget.flights) || {};
  const data = (widget && widget.data) || {};
  const airport = flights.airport || data;
  const iata = data.iata || airport.iata || "";
  const customName = String(data.name || "")
    .trim()
    .slice(0, 50);
  const airportName = customName || airport.name || iata || "Airport";
  const maxShow = Math.max(1, parseInt(data.limit, 10) || 100);

  const depSrc =
    (flights.departures && flights.departures.flights) || null;
  const arrSrc =
    (flights.arrivals && flights.arrivals.flights) || null;


  const departures = useMemo(
    () => (depSrc || []).slice(0, maxShow),
    [depSrc, maxShow],
  );
  const arrivals = useMemo(
    () => (arrSrc || []).slice(0, maxShow),
    [arrSrc, maxShow],
  );

  const layout = getDisplayType(window.innerWidth, window.innerHeight);
  const Comp = LAYOUTS[layout] || Landscape;
  const theme = data.theme === "dark" ? "dark" : "light";
  const cfgMode = data.mode || "both";
  const showBoth = cfgMode === "both";
  const pageSize = showBoth
    ? DUAL_PAGE_SIZE[layout] || 5
    : PAGE_SIZE[layout] || 8;

  const depCount = departures.length;
  const arrCount = arrivals.length;

  const initialMode =
    cfgMode === "arrivals"
      ? "arrivals"
      : cfgMode === "departures"
      ? "departures"
      : depCount || !arrCount
      ? "departures"
      : "arrivals";

  const [view, setView] = useState({
    mode: initialMode,
    page: 0,
    depPage: 0,
    arrPage: 0,
  });
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // stable snapshot for interval callback
  const flipRef = useRef({});
  flipRef.current = {
    showBoth,
    maxShow,
    pageSize,
    depCount,
    arrCount,
  };

  useEffect(() => {
    const t = setInterval(() => {
      const {
        showBoth: both,
        pageSize: psz,
        depCount: dCnt,
        arrCount: aCnt,
      } = flipRef.current;

      setView((prev) => {
        if (both) {
          const dPages = Math.max(1, Math.ceil(dCnt / Math.max(psz, 1)));
          const aPages = Math.max(1, Math.ceil(aCnt / Math.max(psz, 1)));
          return {
            ...prev,
            depPage: dCnt > psz ? (prev.depPage + 1) % dPages : 0,
            arrPage: aCnt > psz ? (prev.arrPage + 1) % aPages : 0,
          };
        }

        const { mode, page } = prev;
        const count = mode === "departures" ? dCnt : aCnt;
        const pages = Math.max(1, Math.ceil(count / psz));
        if (pages <= 1) return prev;
        return { ...prev, mode, page: (page + 1) % pages };
      });
    }, PAGE_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setView((prev) => ({
      ...prev,
      mode:
        cfgMode === "arrivals"
          ? "arrivals"
          : cfgMode === "departures"
          ? "departures"
          : prev.mode || initialMode,
      page: 0,
      depPage: 0,
      arrPage: 0,
    }));
  }, [cfgMode, depCount, arrCount, maxShow, pageSize, initialMode]);

  const airportLabel = iata
    ? `${String(airportName).toUpperCase()} - ${iata}`
    : String(airportName).toUpperCase();

  if (showBoth) {
    const depN = Math.min(pageSize, depCount);
    const arrN = Math.min(pageSize, arrCount);
    const depStep = Math.max(depN, 1);
    const arrStep = Math.max(arrN, 1);

    const depSlice =
      depN > 0
        ? departures.slice(
            (view.depPage || 0) * depStep,
            (view.depPage || 0) * depStep + depN,
          )
        : [];
    const arrSlice =
      arrN > 0
        ? arrivals.slice(
            (view.arrPage || 0) * arrStep,
            (view.arrPage || 0) * arrStep + arrN,
          )
        : [];

    const dPages = Math.max(1, Math.ceil(depCount / depStep));
    const aPages = Math.max(1, Math.ceil(arrCount / arrStep));

    return (
      <Comp
        showBoth
        depRows={mapRows(depSlice, true)}
        arrRows={mapRows(arrSlice, false)}
        rows={[]}
        isDep
        theme={theme}
        airportLabel={airportLabel}
        dateLabel={fmtDate(now)}
        timeLabel={fmtClock(now)}
        page={Math.max(view.depPage || 0, view.arrPage || 0)}
        pages={Math.max(dPages, aPages)}
      />
    );
  }

  const isDep = view.mode === "departures";
  const list = isDep ? departures : arrivals;
  const pages = Math.max(1, Math.ceil(list.length / pageSize));
  const page = Math.min(view.page, pages - 1);
  const rows = mapRows(
    list.slice(page * pageSize, page * pageSize + pageSize),
    isDep,
  );

  return (
    <Comp
      rows={rows}
      isDep={isDep}
      theme={theme}
      airportLabel={airportLabel}
      dateLabel={fmtDate(now)}
      timeLabel={fmtClock(now)}
      page={page}
      pages={pages}
    />
  );
};

export default Flight;
